import moment from 'moment';
import { Portfolio, Product, Program } from './program.model';
import { Organization, Site } from './organization.model';

export enum DateOption {
  lastWeek = 'lastWeek',
  lastMonth = 'lastMonth',
  last3Months = 'last3Months',
  //Have to keep the m as lowercase to match dsy-date-picker values
  last6Months = 'last6months',
  lastYear = 'lastYear',
  last8Years = 'last8Years', // for testing purposes
  customPeriod = 'customPeriod',
}

type SuggestionSetters = {
  setFromDate: (date: Date) => void;
  setToDate: (date: Date) => void;
};

const setRangeFromOption = (option: DateOption) => (setters: SuggestionSetters) => {
  const { start, end } = getDateRangeFromDateOption(option);
  setters.setFromDate(start.toDate());
  setters.setToDate(end.toDate());
};

export const customDatePickerSuggestionsMap: Partial<Record<DateOption, (setters: SuggestionSetters) => void>> = {
  [DateOption.lastWeek]: setRangeFromOption(DateOption.lastWeek),
  [DateOption.lastMonth]: setRangeFromOption(DateOption.lastMonth),
  [DateOption.last3Months]: setRangeFromOption(DateOption.last3Months),
  [DateOption.last6Months]: setRangeFromOption(DateOption.last6Months),
  [DateOption.lastYear]: setRangeFromOption(DateOption.lastYear),
  // for testing purposes
  [DateOption.last8Years]: setRangeFromOption(DateOption.last8Years),
};

export const DATE_PICKER_OPTIONS: { label: string; value: DateOption }[] = [
  { label: 'PD.DATE_PICKER.LAST_WEEK', value: DateOption.lastWeek },
  { label: 'PD.DATE_PICKER.LAST_MONTH', value: DateOption.lastMonth },
  { label: 'PD.DATE_PICKER.LAST_3_MONTHS', value: DateOption.last3Months },
  { label: 'PD.DATE_PICKER.LAST_6_MONTHS', value: DateOption.last6Months },
  { label: 'PD.DATE_PICKER.LAST_YEAR', value: DateOption.lastYear },
  { label: 'PD.DATE_PICKER.LAST_8_YEARS', value: DateOption.last8Years },
  { label: 'PD.DATE_PICKER.CUSTOM_PERIOD', value: DateOption.customPeriod },
];

export interface DateRange {
  start: moment.Moment;
  end: moment.Moment;
}

export interface SelectedFilters {
  dateRange: DateRange;
  dateOption: DateOption;
}
export interface SelectedProgramFilters extends SelectedFilters {
  program: Program;
  products: Product[];
}

export interface SelectedOrganizationFilters extends SelectedFilters {
  organization: Organization;
  sites: Site[];
}

export function isSelectedOrganizationFilters(filters: SelectedFilters): filters is SelectedOrganizationFilters {
  return (filters as SelectedOrganizationFilters).organization !== undefined;
}

export function isSelectedProgramFilters(filters: SelectedFilters): filters is SelectedProgramFilters {
  return (filters as SelectedProgramFilters).program !== undefined;
}

export function selectedFiltersFromPrograms(programs: Program[], url?: { id?: string; range?: string; from?: string; to?: string }) {
  if (programs.length === 0) return undefined;
  const dateOption = Object.values(DateOption).includes(url?.range as DateOption) ? (url!.range as DateOption) : DateOption.last3Months;
  return {
    program: programs.find((p) => String(p.id) === url?.id) ?? programs[0],
    products: [],
    dateRange: getDateRangeFromDateOption(dateOption, url?.from ? new Date(url.from) : undefined, url?.to ? new Date(url.to) : undefined),
    dateOption,
  };
}

export function selectedFiltersFromOrganizations(organizations: Organization[], url?: { id?: string; range?: string; from?: string; to?: string }): SelectedOrganizationFilters | undefined {
  if (organizations.length === 0) {
    return undefined;
  }
  return {
    organization: organizations.find((o) => String(o.id) === url?.id) ?? organizations[0],
    sites: [],
    dateRange: getDateRangeFromDateOption(
      Object.values(DateOption).includes(url?.range as DateOption) ? (url!.range as DateOption) : DateOption.last3Months,
      url?.from ? new Date(url.from) : undefined,
      url?.to ? new Date(url.to) : undefined
    ),
    dateOption: Object.values(DateOption).includes(url?.range as DateOption) ? (url!.range as DateOption) : DateOption.last3Months,
  };
}

export function getDateRangeFromDateOption(option: DateOption, customStart?: Date, customEnd?: Date): DateRange {
  const end = moment().endOf('day');
  let start: moment.Moment = moment().startOf('day');

  switch (option) {
    case DateOption.lastWeek:
      start = moment().subtract(1, 'week').startOf('day');
      break;
    case DateOption.lastMonth:
      start = moment().subtract(1, 'month').startOf('day');
      break;
    case DateOption.last3Months:
      start = moment().subtract(3, 'months').startOf('day');
      break;
    case DateOption.last6Months:
      start = moment().subtract(6, 'months').startOf('day');
      break;
    case DateOption.lastYear:
      start = moment().subtract(1, 'year').startOf('day');
      break;
    case DateOption.last8Years:
      start = moment().subtract(8, 'years').startOf('day');
      break;
    case DateOption.customPeriod:
      if (customStart && customEnd) {
        return {
          start: moment(customStart).startOf('day'),
          end: moment(customEnd).endOf('day'),
        };
      }
  }
  return { start, end };
}
